import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import Header from '../Header';
import Footer from '../Footer';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { 
  User, Mail, Phone, MapPin, Briefcase, GraduationCap, 
  Building2, Plus, Trash2, Award, BookOpen, Save,
  ArrowLeft, Camera, Globe, Linkedin, X, Loader2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EditConnectProfilePage = () => {
  const navigate = useNavigate();
  const { profileId } = useParams();
  const { user: currentUser, isAuthenticated, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    profile_type: '',
    name: '',
    email: '',
    phone: '',
    whatsapp: '',
    headline: '',
    bio: '',
    location: '',
    website: '',
    linkedin: '',
    lattes: '',
    profile_photo: '',
    skills: [],
    certifications: [],
    experiences: [],
    education: [],
    courses: [],
    company_size: '',
    industry: '',
    services: []
  });

  const [newSkill, setNewSkill] = useState('');
  const [newService, setNewService] = useState('');
  
  // New certification form
  const [newCertification, setNewCertification] = useState({
    name: '',
    issuer: '',
    year: ''
  });
  
  // New education form
  const [newEducation, setNewEducation] = useState({
    institution: '',
    degree: '',
    field: '',
    year: ''
  });
  
  // New course form
  const [newCourse, setNewCourse] = useState({
    name: '',
    institution: '',
    year: ''
  });

  useEffect(() => {
    if (authLoading) return;
    
    if (!isAuthenticated || !currentUser) {
      toast.error('Faça login para editar seu perfil');
      navigate('/connect');
      return;
    }
    
    fetchProfile();
  }, [profileId, currentUser, isAuthenticated, authLoading]);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/connect/profiles/${profileId}`);
      const profile = response.data;
      
      // Check if this is the user's profile
      if (profile.user_id !== currentUser?.id) {
        toast.error('Você não tem permissão para editar este perfil');
        navigate('/connect');
        return;
      }
      
      setFormData({
        profile_type: profile.profile_type || '',
        name: profile.name || '',
        email: profile.email || '',
        phone: profile.phone || '',
        whatsapp: profile.whatsapp || '',
        headline: profile.headline || '',
        bio: profile.bio || '',
        location: profile.location || '',
        website: profile.website || '',
        linkedin: profile.linkedin || '',
        lattes: profile.lattes || '',
        profile_photo: profile.profile_photo || '',
        skills: profile.skills || [],
        certifications: profile.certifications || [],
        experiences: profile.experiences || [],
        education: profile.education || [],
        courses: profile.courses || [],
        company_size: profile.company_size || '',
        industry: profile.industry || '',
        services: profile.services || []
      });
    } catch (error) {
      toast.error('Erro ao carregar perfil');
      navigate('/connect');
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Formato inválido. Envie JPG, PNG ou WEBP.');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('A imagem deve ter no máximo 5 MB.');
      return;
    }

    // Recomendação de dimensões mínimas (não bloqueia)
    const img = new Image();
    img.onload = () => {
      if (img.width < 400 || img.height < 400) {
        toast.warning('Recomendamos imagens de no mínimo 400×400px para melhor qualidade.');
      }
    };
    img.src = URL.createObjectURL(file);

    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/connect/upload-image`, fd);
      setFormData(prev => ({ ...prev, profile_photo: res.data.url }));
      toast.success('Imagem enviada! Clique em "Salvar Alterações" para confirmar.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar imagem');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleAddSkill = () => {
    if (newSkill.trim() && !formData.skills.includes(newSkill.trim())) {
      setFormData(prev => ({
        ...prev,
        skills: [...prev.skills, newSkill.trim()]
      }));
      setNewSkill('');
    }
  };

  const handleRemoveSkill = (skill) => {
    setFormData(prev => ({
      ...prev,
      skills: prev.skills.filter(s => s !== skill)
    }));
  };

  const handleAddService = () => {
    if (newService.trim() && !formData.services.includes(newService.trim())) {
      setFormData(prev => ({
        ...prev,
        services: [...prev.services, newService.trim()]
      }));
      setNewService('');
    }
  };

  const handleRemoveService = (service) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.filter(s => s !== service)
    }));
  };

  // Certification handlers
  const handleAddCertification = () => {
    if (newCertification.name.trim() && newCertification.issuer.trim()) {
      setFormData(prev => ({
        ...prev,
        certifications: [...prev.certifications, { ...newCertification, id: Date.now() }]
      }));
      setNewCertification({ name: '', issuer: '', year: '' });
    }
  };

  const handleRemoveCertification = (id) => {
    setFormData(prev => ({
      ...prev,
      certifications: prev.certifications.filter(c => c.id !== id)
    }));
  };

  // Education handlers
  const handleAddEducation = () => {
    if (newEducation.institution.trim() && newEducation.degree.trim()) {
      setFormData(prev => ({
        ...prev,
        education: [...prev.education, { ...newEducation, id: Date.now() }]
      }));
      setNewEducation({ institution: '', degree: '', field: '', year: '' });
    }
  };

  const handleRemoveEducation = (id) => {
    setFormData(prev => ({
      ...prev,
      education: prev.education.filter(e => e.id !== id)
    }));
  };

  // Course handlers
  const handleAddCourse = () => {
    if (newCourse.name.trim() && newCourse.institution.trim()) {
      setFormData(prev => ({
        ...prev,
        courses: [...prev.courses, { ...newCourse, id: Date.now() }]
      }));
      setNewCourse({ name: '', institution: '', year: '' });
    }
  };

  const handleRemoveCourse = (id) => {
    setFormData(prev => ({
      ...prev,
      courses: prev.courses.filter(c => c.id !== id)
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    
    try {
      await axios.put(`${API}/connect/profiles/${profileId}`, formData);
      toast.success('Perfil atualizado com sucesso!');
      navigate(`/connect/profile/${profileId}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar perfil');
    } finally {
      setSaving(false);
    }
  };

  const skillSuggestions = [
    'Revit', 'AutoCAD', 'Navisworks', 'BIM 360', 'ArchiCAD', 
    'SketchUp', 'Dynamo', 'Coordenação BIM', 'Modelagem 3D',
    'LOD', 'ISO 19650', 'Clash Detection', 'Quantitativos'
  ];

  const getProfileTypeLabel = (type) => {
    const types = {
      student: 'Estudante',
      professional: 'Profissional',
      company: 'Empresa',
      institution: 'Instituição'
    };
    return types[type] || type;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black">
        <Header />
        <div className="pt-24 pb-16 flex items-center justify-center">
          <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      <Header />
      
      <section className="pt-24 pb-16">
        <div className="container mx-auto px-4 max-w-3xl">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                onClick={() => navigate(`/connect/profile/${profileId}`)}
                className="text-gray-400 hover:text-white"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Voltar
              </Button>
              <h1 className="text-2xl font-bold text-white">Editar Perfil</h1>
            </div>
            <Badge className="bg-blue-600/20 text-blue-400">
              {getProfileTypeLabel(formData.profile_type)}
            </Badge>
          </div>

          {/* Photo Section */}
          <Card className="bg-zinc-900 border-zinc-800 mb-6">
            <CardContent className="p-6">
              <div className="flex items-center gap-6">
                <div className="relative">
                  <div className="w-24 h-24 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white text-3xl font-bold overflow-hidden">
                    {formData.profile_photo ? (
                      <img 
                        src={formData.profile_photo} 
                        alt={formData.name} 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      formData.name?.charAt(0)?.toUpperCase() || <User className="w-10 h-10" />
                    )}
                  </div>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingPhoto}
                    className="absolute bottom-0 right-0 w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white hover:bg-blue-700 transition-colors"
                    data-testid="upload-photo-btn"
                  >
                    {uploadingPhoto ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Camera className="w-4 h-4" />
                    )}
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handlePhotoUpload}
                    className="hidden"
                  />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">
                    {(formData.profile_type === 'company' || formData.profile_type === 'institution') ? 'Logomarca' : 'Foto do Perfil'}
                  </h3>
                  <p className="text-sm text-gray-400">Clique no ícone para alterar</p>
                  <p className="text-xs text-gray-500 mt-1 max-w-sm">
                    Tipos aceitos: JPG, PNG e WEBP; Tamanho máximo: 5 MB; Dimensões recomendadas: mínimo 400×400px.
                    {(formData.profile_type === 'company' || formData.profile_type === 'institution') && ' Para logomarca, recomendamos PNG com fundo transparente.'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Basic Info */}
          <Card className="bg-zinc-900 border-zinc-800 mb-6">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <User className="w-5 h-5 text-blue-500" />
                Informações Básicas
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Nome completo *</label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="bg-zinc-800 border-zinc-700 text-white"
                    data-testid="edit-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Email *</label>
                  <Input
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    className="bg-zinc-800 border-zinc-700 text-white"
                    type="email"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Telefone</label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="(11) 99999-9999"
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">WhatsApp</label>
                  <Input
                    value={formData.whatsapp}
                    onChange={(e) => setFormData(prev => ({ ...prev, whatsapp: e.target.value }))}
                    placeholder="(11) 99999-9999"
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Título profissional</label>
                <Input
                  value={formData.headline}
                  onChange={(e) => setFormData(prev => ({ ...prev, headline: e.target.value }))}
                  placeholder="Ex: BIM Manager | Especialista Revit"
                  className="bg-zinc-800 border-zinc-700 text-white"
                  data-testid="edit-headline-input"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Localização</label>
                  <Input
                    value={formData.location}
                    onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                    placeholder="Cidade, Estado"
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Website</label>
                  <Input
                    value={formData.website}
                    onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
                    placeholder="https://seusite.com"
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">LinkedIn</label>
                <Input
                  value={formData.linkedin}
                  onChange={(e) => setFormData(prev => ({ ...prev, linkedin: e.target.value }))}
                  placeholder="https://linkedin.com/in/seuperfil"
                  className="bg-zinc-800 border-zinc-700 text-white"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Currículo Lattes</label>
                <Input
                  value={formData.lattes}
                  onChange={(e) => setFormData(prev => ({ ...prev, lattes: e.target.value }))}
                  placeholder="https://lattes.cnpq.br/0000000000000000"
                  className="bg-zinc-800 border-zinc-700 text-white"
                  data-testid="edit-lattes-input"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Sobre você</label>
                <textarea
                  value={formData.bio}
                  onChange={(e) => setFormData(prev => ({ ...prev, bio: e.target.value }))}
                  placeholder="Conte um pouco sobre sua experiência e objetivos..."
                  rows={4}
                  className="w-full bg-zinc-800 border border-zinc-700 text-white rounded-md px-3 py-2 resize-none"
                  data-testid="edit-bio-input"
                />
              </div>
            </CardContent>
          </Card>

          {/* Skills */}
          <Card className="bg-zinc-900 border-zinc-800 mb-6">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Award className="w-5 h-5 text-yellow-500" />
                Habilidades
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Input
                  value={newSkill}
                  onChange={(e) => setNewSkill(e.target.value)}
                  placeholder="Adicionar habilidade"
                  className="bg-zinc-800 border-zinc-700 text-white"
                  onKeyPress={(e) => e.key === 'Enter' && handleAddSkill()}
                />
                <Button onClick={handleAddSkill} className="bg-blue-600 hover:bg-blue-700">
                  <Plus className="w-5 h-5" />
                </Button>
              </div>
              
              {/* Suggestions */}
              <div className="flex flex-wrap gap-2">
                {skillSuggestions.filter(s => !formData.skills.includes(s)).slice(0, 8).map(skill => (
                  <Badge
                    key={skill}
                    className="bg-zinc-800 text-gray-400 cursor-pointer hover:bg-zinc-700"
                    onClick={() => setFormData(prev => ({ ...prev, skills: [...prev.skills, skill] }))}
                  >
                    + {skill}
                  </Badge>
                ))}
              </div>

              {/* Selected skills */}
              {formData.skills.length > 0 && (
                <div className="flex flex-wrap gap-2 pt-2 border-t border-zinc-800">
                  {formData.skills.map(skill => (
                    <Badge key={skill} className="bg-blue-600/20 text-blue-400">
                      {skill}
                      <button
                        onClick={() => handleRemoveSkill(skill)}
                        className="ml-2 hover:text-red-400"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Certifications */}
          <Card className="bg-zinc-900 border-zinc-800 mb-6">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Award className="w-5 h-5 text-orange-500" />
                Certificações
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Add new certification */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <Input
                  value={newCertification.name}
                  onChange={(e) => setNewCertification(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Nome da certificação"
                  className="bg-zinc-800 border-zinc-700 text-white md:col-span-2"
                />
                <Input
                  value={newCertification.issuer}
                  onChange={(e) => setNewCertification(prev => ({ ...prev, issuer: e.target.value }))}
                  placeholder="Instituição emissora"
                  className="bg-zinc-800 border-zinc-700 text-white"
                />
                <div className="flex gap-2">
                  <Input
                    value={newCertification.year}
                    onChange={(e) => setNewCertification(prev => ({ ...prev, year: e.target.value }))}
                    placeholder="Ano"
                    className="bg-zinc-800 border-zinc-700 text-white w-24"
                  />
                  <Button onClick={handleAddCertification} className="bg-orange-600 hover:bg-orange-700">
                    <Plus className="w-5 h-5" />
                  </Button>
                </div>
              </div>

              {/* List certifications */}
              {formData.certifications.length > 0 && (
                <div className="space-y-3 pt-4 border-t border-zinc-800">
                  {formData.certifications.map((cert, index) => (
                    <div key={cert.id || index} className="flex items-center justify-between bg-zinc-800/50 rounded-lg p-3">
                      <div>
                        <p className="text-white font-medium">{cert.name}</p>
                        <p className="text-sm text-gray-400">{cert.issuer} {cert.year && `• ${cert.year}`}</p>
                      </div>
                      <button
                        onClick={() => handleRemoveCertification(cert.id || index)}
                        className="text-gray-400 hover:text-red-400"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              {formData.certifications.length === 0 && (
                <p className="text-gray-500 text-sm text-center py-4">
                  Nenhuma certificação adicionada. Ex: Autodesk Certified Professional, ISO 19650, etc.
                </p>
              )}
            </CardContent>
          </Card>

          {/* Education / Formação Acadêmica */}
          <Card className="bg-zinc-900 border-zinc-800 mb-6">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <GraduationCap className="w-5 h-5 text-purple-500" />
                Formação Acadêmica
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Add new education */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Input
                  value={newEducation.institution}
                  onChange={(e) => setNewEducation(prev => ({ ...prev, institution: e.target.value }))}
                  placeholder="Instituição de ensino"
                  className="bg-zinc-800 border-zinc-700 text-white"
                />
                <Input
                  value={newEducation.degree}
                  onChange={(e) => setNewEducation(prev => ({ ...prev, degree: e.target.value }))}
                  placeholder="Grau (Graduação, Pós, Mestrado...)"
                  className="bg-zinc-800 border-zinc-700 text-white"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Input
                  value={newEducation.field}
                  onChange={(e) => setNewEducation(prev => ({ ...prev, field: e.target.value }))}
                  placeholder="Área de estudo"
                  className="bg-zinc-800 border-zinc-700 text-white md:col-span-2"
                />
                <div className="flex gap-2">
                  <Input
                    value={newEducation.year}
                    onChange={(e) => setNewEducation(prev => ({ ...prev, year: e.target.value }))}
                    placeholder="Ano"
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                  <Button onClick={handleAddEducation} className="bg-purple-600 hover:bg-purple-700">
                    <Plus className="w-5 h-5" />
                  </Button>
                </div>
              </div>

              {/* List education */}
              {formData.education.length > 0 && (
                <div className="space-y-3 pt-4 border-t border-zinc-800">
                  {formData.education.map((edu, index) => (
                    <div key={edu.id || index} className="flex items-center justify-between bg-zinc-800/50 rounded-lg p-3">
                      <div>
                        <p className="text-white font-medium">{edu.degree} {edu.field && `em ${edu.field}`}</p>
                        <p className="text-sm text-gray-400">{edu.institution} {edu.year && `• ${edu.year}`}</p>
                      </div>
                      <button
                        onClick={() => handleRemoveEducation(edu.id || index)}
                        className="text-gray-400 hover:text-red-400"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              {formData.education.length === 0 && (
                <p className="text-gray-500 text-sm text-center py-4">
                  Nenhuma formação adicionada. Ex: Engenharia Civil, Arquitetura, etc.
                </p>
              )}
            </CardContent>
          </Card>

          {/* Complementary Courses */}
          <Card className="bg-zinc-900 border-zinc-800 mb-6">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-green-500" />
                Cursos Complementares
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Add new course */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <Input
                  value={newCourse.name}
                  onChange={(e) => setNewCourse(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Nome do curso"
                  className="bg-zinc-800 border-zinc-700 text-white md:col-span-2"
                />
                <Input
                  value={newCourse.institution}
                  onChange={(e) => setNewCourse(prev => ({ ...prev, institution: e.target.value }))}
                  placeholder="Instituição"
                  className="bg-zinc-800 border-zinc-700 text-white"
                />
                <div className="flex gap-2">
                  <Input
                    value={newCourse.year}
                    onChange={(e) => setNewCourse(prev => ({ ...prev, year: e.target.value }))}
                    placeholder="Ano"
                    className="bg-zinc-800 border-zinc-700 text-white w-24"
                  />
                  <Button onClick={handleAddCourse} className="bg-green-600 hover:bg-green-700">
                    <Plus className="w-5 h-5" />
                  </Button>
                </div>
              </div>

              {/* List courses */}
              {formData.courses.length > 0 && (
                <div className="space-y-3 pt-4 border-t border-zinc-800">
                  {formData.courses.map((course, index) => (
                    <div key={course.id || index} className="flex items-center justify-between bg-zinc-800/50 rounded-lg p-3">
                      <div>
                        <p className="text-white font-medium">{course.name}</p>
                        <p className="text-sm text-gray-400">{course.institution} {course.year && `• ${course.year}`}</p>
                      </div>
                      <button
                        onClick={() => handleRemoveCourse(course.id || index)}
                        className="text-gray-400 hover:text-red-400"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              {formData.courses.length === 0 && (
                <p className="text-gray-500 text-sm text-center py-4">
                  Nenhum curso complementar adicionado. Ex: Revit Avançado, BIM Manager, etc.
                </p>
              )}
            </CardContent>
          </Card>

          {/* Services (for companies) */}
          {(formData.profile_type === 'company' || formData.profile_type === 'institution') && (
            <Card className="bg-zinc-900 border-zinc-800 mb-6">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-green-500" />
                  Serviços Oferecidos
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    value={newService}
                    onChange={(e) => setNewService(e.target.value)}
                    placeholder="Adicionar serviço"
                    className="bg-zinc-800 border-zinc-700 text-white"
                    onKeyPress={(e) => e.key === 'Enter' && handleAddService()}
                  />
                  <Button onClick={handleAddService} className="bg-green-600 hover:bg-green-700">
                    <Plus className="w-5 h-5" />
                  </Button>
                </div>

                {formData.services.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {formData.services.map(service => (
                      <Badge key={service} className="bg-green-600/20 text-green-400">
                        {service}
                        <button
                          onClick={() => handleRemoveService(service)}
                          className="ml-2 hover:text-red-400"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Save Button */}
          <div className="flex justify-end gap-4">
            <Button
              variant="outline"
              onClick={() => navigate(`/connect/profile/${profileId}`)}
              className="border-zinc-700 text-gray-300"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="save-profile-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Salvando...
                </>
              ) : (
                <>
                  <Save className="w-5 h-5 mr-2" />
                  Salvar Alterações
                </>
              )}
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default EditConnectProfilePage;
